import get from 'lodash/get';
import React, {PureComponent, Fragment} from 'react';
import PropTypes from 'prop-types';
import {CREDIT_WALLET_DATA} from 'user/user-prop-types';
import {PROMO_CODE_DATA} from 'checkout/checkout-prop-types';
import {connect} from 'react-redux';
import classNames from 'classnames';
import Alert from '@spothero/ui/alert';
import CheckoutUtils from 'checkout/checkout-utils';
import Price from 'common/price/Price';
import {getCreditWallet} from 'checkout/checkout-selectors';
import PriceBreakdownItem from './PriceBreakdownItem';
import PriceBreakdownItemRollup from './PriceBreakdownItemRollup';

class ReviewPurchase extends PureComponent {
    static propTypes = {
        ...CREDIT_WALLET_DATA,
        isMonthly: PropTypes.bool.isRequired,
        starts: PropTypes.string.isRequired,
        promoCode: PropTypes.shape(PROMO_CODE_DATA),
        selectedRate: PropTypes.object,
        showTaxesAndFeesMessage: PropTypes.bool,
    };

    static defaultProps = {
        showTaxesAndFeesMessage: false,
    };

    _renderMonthlyPriceLabel = () => {
        const {currencyType, isMonthly, selectedRate, starts} = this.props;
        const {startDateFormatted, endDateFormatted} = isMonthly
            ? CheckoutUtils.getMonthlyReservationInfo(selectedRate, starts)
            : {
                  startDateFormatted: null,
                  endDateFormatted: null,
              };
        const subtotal = get(selectedRate, 'price', null);

        return (
            <div className="ReviewPurchase-line-item">
                <span className="ReviewPurchase-label">
                    {startDateFormatted} - {endDateFormatted}
                </span>
                <Price
                    amount={subtotal}
                    currencyType={currencyType}
                    showCents
                />
            </div>
        );
    };

    _renderPriceBreakdownItems = () => {
        const {selectedRate, currencyType} = this.props;
        const priceBreakdownItems = get(selectedRate, 'price_breakdown.items');

        return priceBreakdownItems
            ? priceBreakdownItems.map((item, i) => {
                  return item.sub_items ? (
                      <PriceBreakdownItemRollup
                          key={i}
                          item={item}
                          currencyType={currencyType}
                      />
                  ) : (
                      <PriceBreakdownItem
                          key={i}
                          item={item}
                          currencyType={currencyType}
                      />
                  );
              })
            : null;
    };

    render() {
        const {
            credit,
            currencyType,
            isMonthly,
            paymentRequired,
            promoCode,
            selectedRate,
            showTaxesAndFeesMessage,
        } = this.props;
        const amountToChargeClasses = classNames(
            'ReviewPurchase-line-item',
            'ReviewPurchase-amount-to-charge',
            {'ReviewPurchase-amount-to-charge-only': !promoCode && !credit}
        );
        const fallbackPrice = selectedRate ? selectedRate.price : 0;
        const price = !isMonthly
            ? get(selectedRate, 'price_breakdown.total_price', fallbackPrice)
            : fallbackPrice;
        const {amountToCharge, creditUsed} = CheckoutUtils.getAmountToCharge({
            price,
            discount: promoCode ? promoCode.discountAmount * 100 : 0, // TODO bk - should come to us in pennies
            userCredit: credit,
        });

        return (
            <div className="ReviewPurchase">
                {isMonthly && selectedRate && selectedRate.recurrable && (
                    <Alert type="highlight">
                        By clicking &quot;Pay &amp; Reserve&quot; you confirm
                        that this credit card will be charged monthly until you
                        cancel.
                    </Alert>
                )}
                {selectedRate && (
                    <div className="ReviewPurchase-breakdown">
                        {isMonthly
                            ? this._renderMonthlyPriceLabel()
                            : this._renderPriceBreakdownItems()}
                        {(Boolean(promoCode) || Boolean(credit)) && (
                            <Fragment>
                                {Boolean(promoCode) &&
                                    !isNaN(promoCode.discountAmount) &&
                                    paymentRequired && (
                                        <div className="ReviewPurchase-line-item ReviewPurchase-discount-promo">
                                            <span className="ReviewPurchase-label">
                                                <span>
                                                    <span>
                                                        {`Promo ${
                                                            promoCode.codeType ===
                                                            'credit'
                                                                ? 'Credit'
                                                                : 'Code'
                                                        }`}
                                                    </span>{' '}
                                                    Applied
                                                </span>
                                            </span>
                                            <span>
                                                {'-'}
                                                <Price
                                                    amount={
                                                        promoCode.discountAmount *
                                                        100
                                                    }
                                                    currencyType={currencyType}
                                                    showCents
                                                />
                                                {((promoCode.codeType ===
                                                    'credit' &&
                                                    creditUsed < 0) ||
                                                    (promoCode.codeType !==
                                                        'credit' &&
                                                        promoCode.discountAmount >
                                                            selectedRate.price /
                                                                100)) && (
                                                    <sup>*</sup>
                                                )}
                                            </span>
                                        </div>
                                    )}
                                {paymentRequired &&
                                    (Boolean(credit) && creditUsed > 0) && (
                                        <div className="ReviewPurchase-line-item ReviewPurchase-discount-credit">
                                            <span className="ReviewPurchase-label">
                                                SpotHero Credit
                                            </span>
                                            <span>
                                                {'-'}
                                                <Price
                                                    amount={creditUsed}
                                                    currencyType={currencyType}
                                                    showCents
                                                />
                                            </span>
                                        </div>
                                    )}
                            </Fragment>
                        )}
                        <h3 className={amountToChargeClasses}>
                            <span className="ReviewPurchase-label">Total</span>{' '}
                            <Price
                                amount={paymentRequired ? amountToCharge : 0}
                                currencyType={currencyType}
                                showCents={
                                    paymentRequired && amountToCharge !== 0
                                }
                            />
                        </h3>
                        {showTaxesAndFeesMessage && (
                            <small>All taxes and fees are included</small>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        checkout: {paymentRequired, promoCode},
        searchRequest: {monthly: isMonthly, starts},
        spot: {
            selected: {selectedRate},
        },
    } = state;

    return {
        ...getCreditWallet(state),
        selectedRate,
        isMonthly,
        starts,
        paymentRequired,
        promoCode,
    };
};

export default connect(mapStateToProps)(ReviewPurchase);
